import {
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import {
  AddCircle,
  CheckCircle,
  MoreHoriz,
  RefreshRounded,
  RemoveCircle,
  Security,
} from '@material-ui/icons';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CreateIcon from '@material-ui/icons/Create';
import SearchIcon from '@material-ui/icons/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { sortBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { USER } from '../../../../constants/CommonConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_PERMISSION_LEVEL_NO_ACCESS,
  RBAC_TYPE_USERS,
  USER_INACTIVE,
  USER_INVITE,
  USER_STATUS,
  USER_VIEW,
} from '../../../../constants/RoleConsts';
import ActivationPopupModel from '../../../../shared/activation-popup/Activation-Popup';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import ListFilterMenu from '../../../../shared/list-filter-menu/List-Filter-Menu';
import TablePaginationActions from '../../../../shared/table-pagination/TablePaginationActions';
import { notificationManager } from '../../../../utils/NotificationUtils';
import {
  deactivateUserById,
  enableMFA,
  getUsers,
  onDeleteUser,
  reactivateUserById,
  resendUserInvite,
} from '../../../../utils/PropertyAPIUtils';
import './Users-List.scss';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { checkAuthState } from '../../../../utils/APIUtils';
import BulkActionCheckBox from '../../../../shared/MultiActionPopUp/BulkActionCheckBox';
import MultipleDeleteConfirmationPopup from '../../../../shared/MultiActionPopUp/MultiDeleteConfirmationPopup';
import {
  PARTIAL_CHECKBOX_GREEN,
} from '../../../../utils/formUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ENVIRONMENT } from '../../../../env/Configurations';
import { selectedHotelSelector } from '../../../property/propertySlice';

const UsersList = (props) => {
  const {
    onToggle,
    permissions,
    lstRoles,
    onsetPage,
    page,
    onsetRowsPerPage,
    rowsPerPage,
    refreshTick = 0,
  } = props;
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    type: null,
  });
  const [originalUsers, setOriginalUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredBaseList, setFilteredBaseList] = useState([]);
  const loggedinUser = decryptLocalStorage(USER);
  const [filterTargetButton, setFilterTargetButton] = useState(null);
  const [filterMenu, setFilterMenu] = useState([]);
  const [tempSearchText, setTempSearchText] = useState('');
  const [searchButton, setSearchButton] = useState(true);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showMfaConfirmation, setShowMfaConfirmation] = useState(false);
  const [mfaAction, setMfaAction] = useState(null);
  const [cognitoUser, setCognitoUser] = useState(null);
  const dispatch = useDispatch();
  const selectedHotel = useSelector(selectedHotelSelector);
  const prevHotelIdRef = useRef(selectedHotel);
  const skipSearchResetRef = useRef(false);
  
  // Roles that have access to MFA settings
  const rolesWithMfaAccess = new Set(['SUPER_ADMIN', 'IT_TEAM', 'IT_MANAGER', 'IMPLEMENTATION_TEAM']);
  let userRoles = [];
  try {
    userRoles = JSON.parse(cognitoUser?.['custom:roles'] || '[]');
  } catch (e) {
    userRoles = [];
  }
 
  const hasUserMfaAccess = cognitoUser && (
    rolesWithMfaAccess.has(cognitoUser['custom:role']) || 
    (Array.isArray(userRoles) && userRoles.some(role => rolesWithMfaAccess.has(role)))
  );
  
  const selectedUsers = users.filter(user => user.isSelected);
  const selectedUserIds = selectedUsers.map(user => user.id);

  useEffect(() => {
    if (originalUsers?.length && !skipSearchResetRef.current) {
      setUsers(originalUsers);
      setTempSearchText('');
      setSearchButton(true);
    }
    skipSearchResetRef.current = false;
  }, [originalUsers]);
  
  useEffect(() => {
    (async () => {
      const currUser = await checkAuthState();
      if (currUser && currUser.signInUserSession) {
        const idToken = currUser.signInUserSession.getIdToken();
        const idTokenPayload = idToken.payload;
        setCognitoUser(idTokenPayload);
      }
    })();
  }, []);

  useEffect(() => {
    fetchUserList();
  }, []);

  useEffect(() => {
    if (refreshTick > 0) {
      fetchUserList();
    }
  }, [refreshTick]);

  useEffect(() => {
    const updatedFilterMenu = [];
    const filterStatus = [
      { id: 'active', name: 'Active', selected: false },
      { id: 'invited', name: 'Invited', selected: false },
      { id: 'deactivate', name: 'Deactivated', selected: false },
    ];

    const filterAccessLevels = lstRoles.map((obj) => {
      return { id: obj.name, name: obj.name, selected: false };
    });
    filterAccessLevels.push({
      id: 'Custom Role',
      name: 'Custom Role',
      selected: false,
    });

    updatedFilterMenu.push({ title: 'All', field: 'all', subMenu: [] });
    updatedFilterMenu.push({
      title: 'Access',
      field: 'access',
      subMenu: filterAccessLevels,
    });
    updatedFilterMenu.push({
      title: 'Status',
      field: 'status',
      subMenu: filterStatus,
    });

    setFilterMenu(updatedFilterMenu);
  }, [originalUsers, lstRoles]);

  useEffect(() => {
    if (users?.length === 0) return;
    const totalItems = users?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [users?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const setSearchText = (searchText) => {
    setTempSearchText(searchText);
    
    const filteredRows = filteredBaseList.filter((item) => {
      if (!searchText) return true;
      const fullName = `${item.firstName.toLowerCase()} ${item.lastName.toLowerCase()}`;
      return (
        item.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
        item.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
        item.role.toLowerCase().includes(searchText.toLowerCase()) ||
        fullName.includes(searchText.toLowerCase()) ||
        item.email.toLowerCase().includes(searchText.toLowerCase())
      );
    });
    
    onsetPage(0);
    setUsers(filteredRows);
    
    const selectedInFiltered = filteredRows.filter(user => user.isSelected).length;
    setIsAllSelected(selectedInFiltered === filteredRows.length && filteredRows.length > 0);
  };

  const fetchUserList = async () => {
    try {
      dispatch(stepUpLoaderCount());
      const result = await getUsers();
      const sortedUsers = sortBy(result.data.getAllUsers, [(user) => user.firstName.toLowerCase()]);
      
      setOriginalUsers(sortedUsers);
      setFilteredBaseList(sortedUsers);
    } catch (err) {
      if (ENVIRONMENT === 'dev') {
        console.error(err?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (type) => {
    onToggle(type, true, selectedValue.user);
    handleClose();
  };

  const handleCellMenuClick = (event, user) => {
    setSelectedValue({
      id: user.id,
      name: user.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      user: user,
      type: null,
    });
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      user: null,
      type: null,
    });
  };

  const handleDeactivateActivatePopupOpen = () => {
    handleClose();
    setSelectedValue({
      id: selectedValue.id,
      name: selectedValue.name,
      title:
        selectedValue.user.status === 'deactivate'
          ? 'Are you sure you want to reactivate?'
          : 'Are you sure you want to deactivate?',
      description: 'This action cannot be reversed.',
      status: true,
      anchorEl: null,
      user: selectedValue.user,
      type: 'status',
    });
  };

  const deactivateUser = async (user) => {
    try {
      dispatch(stepUpLoaderCount());
      await deactivateUserById(user.id);
      notificationManager('success', 'User deactivated successfully');
      fetchUserList(); // Refresh
      handleClose();
    } catch (err) {
      notificationManager('danger', 'User deactivation failed');
      handleClose();
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const reactivateUser = async (user) => {
    try {
      dispatch(stepUpLoaderCount());
      await reactivateUserById(user.id);
      notificationManager('success', 'User reactivated successfully');
      fetchUserList();
      handleClose();
    } catch (err) {
      notificationManager('danger', 'User reactivation failed');
      handleClose();
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onResendInvitation = async (user) => {
    try {
      dispatch(stepUpLoaderCount());
      await resendUserInvite(user.id);
      notificationManager('success', 'Invitation resent successfully');
    } catch (err) {
      notificationManager('danger', 'Invitation resent failed');

      if (ENVIRONMENT === 'dev') {
        console.log(err?.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onActivationPopupActionCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      type: null,
    });
  };

  const onViewDetails = (user, e, ignoreClass) => {
    if (ignoreClass) onToggle(USER_VIEW, true, user);
    if (e.target.className === 'MuiTableCell-root MuiTableCell-body') {
      onToggle(USER_VIEW, true, user);
    }
  };

  const handleFilterMenu = (event) => {
    setFilterTargetButton(event.currentTarget);
    // setAnchorEl(event.currentTarget);
  };

  const handleFilter = (filterItems, field) => {
    const updatedFilterMenu = [...filterMenu];
    if (field === 'all') {
      const defaultFilterList = updatedFilterMenu.map((filtrMenuData) => {
        let subMenu = [];
        if (filtrMenuData.subMenu && filtrMenuData.subMenu.length > 0) {
          subMenu = filtrMenuData.subMenu.map((subMenuData) => {
            return { ...subMenuData, selected: false };
          });
        }
        return { ...filtrMenuData, subMenu };
      });
      setFilterMenu(defaultFilterList);
      onsetPage(0);
      setFilteredBaseList(originalUsers);
      setUsers(originalUsers);
      return true;
    }
    updatedFilterMenu[updatedFilterMenu.findIndex((o) => o.field === field)].subMenu = filterItems;
    setFilterMenu(updatedFilterMenu);
    const filterIdWithFields = [...updatedFilterMenu]
      .map((filterItem) => {
        const subMenuFiltersId = filterItem.subMenu
          ?.filter((fItem) => fItem.selected === true)
          .map((fItemId) => fItemId.id);
        if (subMenuFiltersId && subMenuFiltersId.length > 0) {
          return {
            id: subMenuFiltersId,
            field: filterItem.field === 'access' ? 'role' : filterItem.field,
          };
        } else return null;
      })
      .filter((item) => item !== null);

    const filteredRecords = originalUsers.filter((user) => {
      const doesFilterSatisfy = filterIdWithFields.map((filter) =>
        filter.id.includes(user[filter.field]),
      );
      return !doesFilterSatisfy.includes(false);
    });
    onsetPage(0);
    setFilteredBaseList(filteredRecords);
    setUsers(filteredRecords);
  };

  const handleDeletePopup = (user) => {
    handleClose();
    setSelectedValue({
      id: selectedValue.id,
      name: selectedValue.name,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed.',
      status: true,
      anchorEl: null,
      user: selectedValue.user,
      type: 'delete',
    });
  };

  const onDelete = async () => {
    try {
      dispatch(stepUpLoaderCount());
      await onDeleteUser(selectedValue.id);
      notificationManager('success', 'User deleted successfully');
      fetchUserList();
      handleClose();
    } catch (e) {
      notificationManager('danger', 'Unable to delete user');
      handleClose();
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onDeleteCancel = () => {
    handleClose();
  };
  
  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setIsAllSelected(isChecked);
    
    const updateUserSelection = (userList) => 
      userList.map(user => ({ ...user, isSelected: isChecked }));
    
    setUsers(updateUserSelection(users));
    setFilteredBaseList(updateUserSelection(filteredBaseList));
  };
  
  const handleUserSelection = (userId, isChecked) => {
    const updateUserInList = (userList) => 
      userList.map(user => 
        user.id === userId ? { ...user, isSelected: isChecked } : user
      );
    
    const updatedUsers = updateUserInList(users);
    const updatedFilteredBaseList = updateUserInList(filteredBaseList);
    
    setUsers(updatedUsers);
    setFilteredBaseList(updatedFilteredBaseList);
    
    const selectedCount = updatedUsers.filter(user => user.isSelected).length;
    setIsAllSelected(selectedCount === updatedUsers.length && updatedUsers.length > 0);
  };
  
  const handleMfaEnforcement = (enforce) => {
    if (selectedUserIds.length === 0) {
      notificationManager('warning', 'Please select at least one user');
      return;
    }
    
    setMfaAction(enforce);
    setShowMfaConfirmation(true);
  };
  
  const confirmMfaEnforcement = async () => {    
    if (!selectedUserIds || selectedUserIds.length === 0) {
      console.error('No users selected for MFA enforcement');
      notificationManager('warning', 'Please select at least one user');
      return;
    }
    
    const currentSearchText = tempSearchText;
    
    try {
      dispatch(stepUpLoaderCount());
      
      const result = await enableMFA(selectedUserIds, mfaAction);
      console.log('enableMFA completed successfully:', result);
      
      notificationManager('success', `MFA ${mfaAction ? 'enforced' : 'disabled'} successfully`);
      
      const refreshResult = await getUsers();
      const sortedUsers = sortBy(refreshResult.data.getAllUsers, [(user) => user.firstName.toLowerCase()]);
      
      skipSearchResetRef.current = true;
      setOriginalUsers(sortedUsers);
      
      const updatedFilteredList = filteredBaseList.map(user => {
        const updatedUser = sortedUsers.find(u => u.id === user.id);
        return updatedUser ? { ...updatedUser, isSelected: false } : { ...user, isSelected: false };
      });
      setFilteredBaseList(updatedFilteredList);
      
      let updatedUsers = users.map(user => {
        const updatedUser = sortedUsers.find(u => u.id === user.id);
        return updatedUser ? { ...updatedUser, isSelected: false } : { ...user, isSelected: false };
      });
      
      if (currentSearchText) {
        updatedUsers = updatedFilteredList.filter((item) => {
          const fullName = `${item.firstName.toLowerCase()} ${item.lastName.toLowerCase()}`;
          return (
            item.firstName.toLowerCase().includes(currentSearchText.toLowerCase()) ||
            item.lastName.toLowerCase().includes(currentSearchText.toLowerCase()) ||
            item.role.toLowerCase().includes(currentSearchText.toLowerCase()) ||
            fullName.includes(currentSearchText.toLowerCase()) ||
            item.email.toLowerCase().includes(currentSearchText.toLowerCase())
          );
        });
      }
      
      setUsers(updatedUsers);
      setIsAllSelected(false);
      
    } catch (error) {
      console.error('MFA enforcement failed:', error);
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to update MFA settings';
      notificationManager('danger', `Failed to update MFA settings: ${errorMessage}`);
      console.error('Full error details:', error);
    } finally {
      dispatch(stepDownLoaderCount());
      setShowMfaConfirmation(false);
      setMfaAction(null);
    }
  };
  
  // Cancel MFA enforcement
  const cancelMfaEnforcement = () => {
    setShowMfaConfirmation(false);
    setMfaAction(null);
  };
  return (
    <div className='mt-4 mb-4 user-list-container'>
      {selectedValue.id && selectedValue.status === true && selectedValue.type === 'status' && (
        <ActivationPopupModel
          activationPopUp={selectedValue}
          user={selectedValue.user}
          onDeactivate={deactivateUser}
          onActivate={reactivateUser}
          onActivationPopupActionCancel={onActivationPopupActionCancel}
          okButtonName={selectedValue?.user?.status === 'deactivate' ? 'Activate' : 'Deactivate'}
        />
      )}
      {selectedValue.id && selectedValue.status === true && selectedValue.type === 'delete' && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={onDelete}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      {showMfaConfirmation && (
        <MultipleDeleteConfirmationPopup
          title={`Are you sure you want to ${mfaAction ? 'enforce' : 'disable'} MFA?`}
          subTitle={`MFA will be ${mfaAction ? 'enforced' : 'disabled'} for ${selectedUserIds.length} selected user${selectedUserIds.length > 1 ? 's' : ''}.`}
          isOpen={showMfaConfirmation}
          onClose={cancelMfaEnforcement}
          onConfirm={confirmMfaEnforcement}
          leftButtonName={'Cancel'}
          rightButtonName={mfaAction ? 'Enforce MFA' : 'Disable MFA'}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col col-lg-4'>
          <TextField
            className='w-100'
            onChange={(e) => {
              setSearchText(e.target.value);
              setSearchButton(e?.target?.value ? false : true);
            }}
            label='Search'
            value={tempSearchText}
            InputProps={
              searchButton && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }
          />
        </div>
        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end gap-2'>
          {users && (
            <>
              <Button
                variant='outlined'
                color='secondary'
                size='large'
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => {
                  handleFilterMenu(e);
                }}
                className={
                  filterMenu?.some((f) => f?.subMenu?.some((s) => s?.selected))
                    ? 'filterButton'
                    : 'dropdown-button'
                }
              >
                Filter
              </Button>
              <ListFilterMenu
                menu={filterMenu}
                targetEl={filterTargetButton}
                handleFilterClose={() => setFilterTargetButton(null)}
                handleFilter={(items, field) => {
                  handleFilter(items, field);
                }}
              />
            </>
          )}
          {hasUserMfaAccess && selectedUserIds.length > 0 && (
            <>
              <Button
                variant='outlined'
                color='secondary'
                size='large'
                startIcon={<Security />}
                onClick={() => handleMfaEnforcement(true)}
              >
                Enforce MFA
              </Button>
              <Button
                variant='outlined'
                color='secondary'
                size='large'
                startIcon={<RemoveCircle />}
                onClick={() => handleMfaEnforcement(false)}
              >
                Disable MFA
              </Button>
            </>
          )}
          {permissions[RBAC_TYPE_USERS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
            <Button
              variant='contained'
              color='primary'
              size='large'
              startIcon={<AddCircle />}
              onClick={(e) => {
                onOpenDrawer(USER_INVITE);
              }}
            >
              Invite New
            </Button>
          )}
        </div>
      </div>
      <div className='row mx-2 d-flex mt-4'>
        <TableContainer>
          <Table className='list-table ' aria-label='simple table'>
            <TableHead>
              <TableRow>
                {hasUserMfaAccess && (
                  <TableCell className='w-5'>
                    <BulkActionCheckBox
                      onChange={handleSelectAll}
                      checked={isAllSelected || false}
                      selectedLength={selectedUserIds.length}
                      totalLength={users.length}
                      inputProps={{
                        'aria-label': 'select all users',
                      }}
                      className={PARTIAL_CHECKBOX_GREEN}
                    />
                  </TableCell>
                )}
                <TableCell className={hasUserMfaAccess ? 'w-20' : 'w-25'}>Name</TableCell>
                <TableCell className={hasUserMfaAccess ? 'w-20' : 'w-25'}>Access</TableCell>
                <TableCell className={hasUserMfaAccess ? 'w-15' : 'w-20'} align='center'>
                  MFA Status
                </TableCell>
                <TableCell className={hasUserMfaAccess ? 'w-20' : 'w-25'} align='center'>
                  Status
                </TableCell>
                <TableCell align='right'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : users
              ).map((user, i) => (
                <TableRow
                  key={i}
                  className='border-white pointer'
                  onClick={(e) => {
                    onViewDetails(user, e, false);
                  }}
                >
                  {hasUserMfaAccess && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <BulkActionCheckBox
                        checked={user.isSelected || false}
                        onChange={(e) => handleUserSelection(user.id, e.target.checked)}
                        selectedLength={user.isSelected ? 1 : 0}
                        totalLength={1}
                        inputProps={{
                          'aria-label': `select ${user?.firstName} ${user?.lastName}`,
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {user?.firstName} {user?.lastName}
                  </TableCell>
                  <TableCell>{user?.role === '' ? 'Custom Role' : user?.role || '---'}</TableCell>
                  <TableCell align='center'>
                    <div className='d-flex justify-content-center'>
                      <div
                        className={`${
                          user?.mfaEnforced === true && 'pill-active'
                        } 
                       ${user?.mfaEnforced === false && 'pill-inactive'} 
                       ${user?.mfaEnforced === null || user?.mfaEnforced === undefined ? 'pill-invited' : ''} 
                       pill d-flex justify-content-center align-items-center`}
                      >
                        {user?.mfaEnforced === true && (
                          <Security fontSize='small' />
                        )}
                        {user?.mfaEnforced === false && (
                          <RemoveCircle fontSize='small' />
                        )}
                        {(user?.mfaEnforced === null || user?.mfaEnforced === undefined) && (
                          <AddCircle fontSize='small' />
                        )}
                        <span className='ms-1'>
                          {user?.mfaEnforced === true
                            ? 'Enabled'
                            : user?.mfaEnforced === false
                            ? 'Disabled'
                            : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    onClick={(e) => {
                      onViewDetails(user, e, true);
                    }}
                    align='center'
                  >
                    <div className='d-flex justify-content-center'>
                      <div
                        className={`${
                          (user?.status === USER_INACTIVE || user?.status === 'deactivate') &&
                          ' pill-inactive'
                        } 
                       ${user?.status === 'active' && 'pill-active'} 
                       ${user?.status === 'invited' && 'pill-invited'} 
                       ${user?.status === '' && 'pill-invited'} 
                       pill d-flex justify-content-center align-items-center`}
                      >
                        {(user?.status === 'invited' || user?.status === '') && (
                          <AddCircle fontSize='small' />
                        )}
                        {user?.status === 'active' && <CheckCircle fontSize='small' />}
                        {user?.status === USER_INACTIVE && <RemoveCircle fontSize='small' />}
                        {user?.status === 'deactivate' && <RemoveCircle fontSize='small' />}
                        <span className='ms-1'>
                          {user?.status === 'deactivate'
                            ? 'Deactivated'
                            : user?.status === 'active'
                            ? 'Active'
                            : user?.status === USER_INACTIVE
                            ? 'Inactive'
                            : 'Invited'}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align='right'>
                    <Button
                      aria-controls='simple-menu'
                      aria-haspopup='true'
                      onClick={(e) => {
                        handleCellMenuClick(e, user);
                      }}
                    >
                      <MoreHoriz />
                    </Button>
                    <Menu
                      id={i}
                      anchorEl={selectedValue?.anchorEl}
                      disableRestoreFocus
                      MenuListProps={{ autoFocusItem: false }}
                      keepMounted
                      open={Boolean(selectedValue.anchorEl) && selectedValue.id === user.id}
                      onClose={handleClose}
                      className='shadow'
                    >
                      {USER_STATUS?.includes(user?.status) &&
                        permissions[RBAC_TYPE_USERS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              handleDeactivateActivatePopupOpen();
                            }}
                          >
                            {selectedValue?.user?.status === 'deactivate' ? (
                              <>
                                <AddCircle className='me-2 action-menu-icon' />
                                Reactivate
                              </>
                            ) : (
                              <>
                                <RemoveCircle className='me-2 action-menu-icon' />
                                Deactivate
                              </>
                            )}
                          </MenuItem>
                        )}
                      {permissions[RBAC_TYPE_USERS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            onOpenDrawer(USER_INVITE);
                          }}
                        >
                          <CreateIcon className='me-2 action-menu-icon' />
                          Edit
                        </MenuItem>
                      )}
                      {permissions[RBAC_TYPE_USERS] > RBAC_PERMISSION_LEVEL_NO_ACCESS && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            onOpenDrawer(USER_VIEW);
                          }}
                        >
                          <VisibilityIcon className='me-2 action-menu-icon' />
                          View
                        </MenuItem>
                      )}
                      {permissions[RBAC_TYPE_USERS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS &&
                        user?.status !== 'active' && (
                          <MenuItem
                            disabled={loggedinUser?.role === 'roleMember'}
                            className='pe-4'
                            onClick={(e) => {
                              onResendInvitation(user);
                            }}
                          >
                            <RefreshRounded className='me-2 action-menu-icon' />
                            Resend Invite
                          </MenuItem>
                        )}
                      {permissions[RBAC_TYPE_USERS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS &&
                        selectedValue?.user?.status === 'deactivate' && (
                          <MenuItem
                            className='pe-4'
                            onClick={(e) => {
                              handleDeletePopup(user);
                            }}
                          >
                            <DeleteForeverIcon className='me-2 action-menu-icon' />
                            Delete
                          </MenuItem>
                        )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className='paginator'>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  count={users.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'Items per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default UsersList;
